import {
  captureException,
  captureMessage,
  withScope,
} from "@/observability/sentry";
import React, { createContext, createRef, useCallback } from "react";
import ReCAPTCHA from "react-google-recaptcha";

const RECAPTCHA_KEY =
  process.env.NEXT_PUBLIC_ENVIRONMENT === "dev"
    ? "6LdeHBAlAAAAAEcwi3J7q756tGukMk3Vq7cTpuOa" // development mode key
    : "6LeHZ6UUAAAAAKat_YS--O2tj_by3gv3r_l03j9d"; // production mode key

const isDev = process.env.NEXT_PUBLIC_ENVIRONMENT === "dev";
interface IRecaptcha {
  onSubmitWithReCAPTCHA: () => Promise<string | null | undefined>;
}

const RecaptchaContextDefaultValue: IRecaptcha = {
  onSubmitWithReCAPTCHA: () =>
    new Promise((resolve) => {
      resolve(undefined);
    }),
};

export const RecaptchaContext = createContext<IRecaptcha>(
  RecaptchaContextDefaultValue
);

export const RecaptchaProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const recaptchaRef = createRef<ReCAPTCHA>();

  const onSubmitWithReCAPTCHA = useCallback(async () => {
    if (isDev) return "...";

    if (!recaptchaRef || !recaptchaRef.current) {
      withScope((scope) => {
        scope.setLevel("error");
        captureMessage("No recaptchaRef or recaptchaRef.current");
      });
      return undefined;
    }

    try {
      /**
       * Sometimes executeAsync can return null if user spends too much time on page
       * and the recaptcha becomes stale.
       *
       * So we're going retry few times before responding if the response is null.
       */
      const MAX_TRIES = 3;
      let token: string | null = null;
      for (let i = 0; i < MAX_TRIES; i++) {
        try {
          if (!recaptchaRef.current) break;
          recaptchaRef.current.reset();
          recaptchaRef.current.getValue();
          token = await recaptchaRef.current.executeAsync();
          // If we get a valid response back break out of the loop
          if (typeof token === "string") break;
          // Expo backoff 0.5, 1, 2 seconds
          await new Promise((r) => setTimeout(r, (2 ^ i) * 250));
        } catch (error) {
          withScope((scope) => {
            scope.setLevel("error");
            scope.setExtra(
              "errorData",
              JSON.stringify(error, Object.getOwnPropertyNames(error || {}))
            );
            captureException(error);
            captureMessage(
              "Recaptcha Provider: Issue getting value from recaptchaRef"
            );
          });
        }
      }

      if (token === null) {
        withScope((scope) => {
          scope.setLevel("error");
          captureMessage(
            "recaptchaRef.current.executeAsync() repeatedly retruned null"
          );
        });
        return null;
      }

      return token;
    } catch (err: unknown) {
      withScope((scope) => {
        scope.setLevel("error");
        captureMessage(`Recaptcha Provider: ${err}`);
        captureException(err as Error);
      });
      return undefined;
    }
  }, [recaptchaRef]);

  return (
    <RecaptchaContext.Provider value={{ onSubmitWithReCAPTCHA }}>
      {children}
      {isDev ? null : (
        <ReCAPTCHA
          size="invisible"
          ref={recaptchaRef}
          sitekey={RECAPTCHA_KEY}
          style={{ display: "none" }}
        />
      )}
    </RecaptchaContext.Provider>
  );
};
